import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, nowChile } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Plus, Loader2, Calendar, BarChart3, Users,
  UserPlus, X, DollarSign, Crown, Clock, Link2, Unlink,
  Target, ExternalLink, AlertTriangle, Upload, Download, FileSpreadsheet
} from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { CalendarView } from '../components/CalendarView';
import { GanttView } from '../components/GanttView';
import { TaskListView } from '../components/TaskListView';

// Full color palette matching user's reference image
const COLOR_PALETTE = [
  ['#E84057', '#EB5B3C', '#E8782A', '#E8A52A', '#D5C52A', '#56C556', '#36B09F', '#3A8DD4', '#5E6BC4', '#9B59B6'],
  ['#F06292', '#FF8A65', '#FFB74D', '#FFD54F', '#E6EE9C', '#81C784', '#4DB6AC', '#64B5F6', '#7986CB', '#BA68C8'],
  ['#F48FB1', '#FFAB91', '#FFCC80', '#FFE082', '#F0F4C3', '#A5D6A7', '#80CBC4', '#90CAF9', '#9FA8DA', '#CE93D8'],
  ['#F8BBD0', '#FFCCBC', '#FFE0B2', '#FFF9C4', '#F9FBE7', '#C8E6C9', '#B2DFDB', '#BBDEFB', '#C5CAE9', '#E1BEE7'],
  ['#880E4F', '#BF360C', '#E65100', '#F57F17', '#827717', '#1B5E20', '#004D40', '#01579B', '#1A237E', '#4A148C'],
  ['#37474F', '#455A64', '#546E7A', '#78909C', '#90A4AE', '#B0BEC5', '#CFD8DC', '#ECEFF1', '#FFFFFF', '#263238'],
];

function flattenTasks(tasks, depth = 0) {
  const result = [];
  for (const t of tasks) {
    result.push({ ...t, _depth: depth });
    if (t.subtasks && t.subtasks.length > 0) {
      result.push(...flattenTasks(t.subtasks, depth + 1));
    }
  }
  return result;
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isJefatura } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [taskDialog, setTaskDialog] = useState(false);
  const [memberDialog, setMemberDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [parentTaskId, setParentTaskId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('gantt');
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [calendarDate, setCalendarDate] = useState(nowChile());
  const [calendarView, setCalendarView] = useState('month');
  const [taskForm, setTaskForm] = useState({
    name: '', description: '', objective: '', url: '', code: '',
    start_date: '', end_date: '',
    assignee_ids: [], color: '#3B82F6', status: 'pendiente', progress: 0, budget: '', is_critical: false
  });

  const fetchProject = useCallback(async () => {
    try {
      const projRes = await axios.get(`${API_URL}/projects/${id}`);
      setProject(projRes.data);
    } catch { toast.error('Error al cargar proyecto'); navigate('/projects'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  const fetchUsers = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/users`); setAllUsers(res.data); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchProject(); fetchUsers(); }, [fetchProject, fetchUsers]);

  const openCreateTask = (pId = null) => {
    setEditingTask(null);
    setParentTaskId(pId);
    setTaskForm({
      name: '', description: '', objective: '', url: '', code: '',
      start_date: format(nowChile(), 'yyyy-MM-dd'),
      end_date: format(addDays(nowChile(), 7), 'yyyy-MM-dd'),
      assignee_ids: [], color: '#3B82F6', status: 'pendiente', progress: 0, budget: '', is_critical: false
    });
    setTaskDialog(true);
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setParentTaskId(task.parent_task_id);
    setTaskForm({
      name: task.name, description: task.description || '',
      objective: task.objective || '', url: task.url || '', code: task.code || '',
      start_date: task.start_date ? task.start_date.split('T')[0] : '',
      end_date: task.end_date ? task.end_date.split('T')[0] : '',
      assignee_ids: (task.assignees || []).map(a => a.user_id),
      color: task.color || '#3B82F6', status: task.status, progress: task.progress,
      budget: task.budget || '', is_critical: task.is_critical || false
    });
    setTaskDialog(true);
  };

  const toggleAssignee = (userId) => {
    setTaskForm(prev => ({
      ...prev,
      assignee_ids: prev.assignee_ids.includes(userId)
        ? prev.assignee_ids.filter(x => x !== userId) : [...prev.assignee_ids, userId]
    }));
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: taskForm.name, description: taskForm.description || null,
        objective: taskForm.objective || null, url: taskForm.url || null,
        code: taskForm.code || null,
        start_date: taskForm.start_date ? `${taskForm.start_date}T00:00:00Z` : null,
        end_date: taskForm.end_date ? `${taskForm.end_date}T00:00:00Z` : null,
        assignee_ids: taskForm.assignee_ids, color: taskForm.color,
        budget: taskForm.budget ? parseFloat(taskForm.budget) : 0,
        is_critical: taskForm.is_critical,
      };
      if (editingTask) {
        payload.status = taskForm.status;
        payload.progress = parseInt(taskForm.progress);
        await axios.patch(`${API_URL}/projects/tasks/${editingTask.id}`, payload);
        toast.success('Tarea actualizada');
      } else {
        payload.parent_task_id = parentTaskId;
        await axios.post(`${API_URL}/projects/${id}/tasks`, payload);
        toast.success(parentTaskId ? 'Subtarea creada' : 'Tarea creada');
      }
      setTaskDialog(false);
      fetchProject();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar tarea'); }
    finally { setSaving(false); }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Eliminar esta tarea y sus subtareas?')) return;
    try { await axios.delete(`${API_URL}/projects/tasks/${taskId}`); toast.success('Tarea eliminada'); fetchProject(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleAddDependency = async (taskId, fromTaskId) => {
    try {
      await axios.post(`${API_URL}/projects/tasks/${taskId}/dependencies`, {
        from_task_id: fromTaskId,
        dependency_type: 'fin_inicio'
      });
      toast.success('Dependencia agregada');
      fetchProject();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al agregar dependencia'); }
  };

  const handleRemoveDependency = async (taskId, depId) => {
    try {
      await axios.delete(`${API_URL}/projects/tasks/${taskId}/dependencies/${depId}`);
      toast.success('Dependencia eliminada');
      fetchProject();
    } catch { toast.error('Error al eliminar dependencia'); }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await axios.post(`${API_URL}/projects/${id}/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      setBulkDialog(false);
      setBulkFile(null);
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar archivo');
    } finally { setBulkUploading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${id}/bulk-template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `plantilla_tareas_${id.slice(0, 8)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar plantilla'); }
  };

  const handleUpdateTaskDates = async (taskId, newStart, newEnd) => {
    try {
      const fmtDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}T00:00:00Z`;
      };
      await axios.patch(`${API_URL}/projects/tasks/${taskId}`, {
        start_date: fmtDate(newStart),
        end_date: fmtDate(newEnd),
      });
      toast.success('Fechas actualizadas');
      fetchProject();
    } catch { toast.error('Error al actualizar fechas'); }
  };

  const handleAddMember = async (userId) => {
    try { await axios.post(`${API_URL}/projects/${id}/members?user_id=${userId}`); toast.success('Miembro agregado'); fetchProject(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleRemoveMember = async (userId) => {
    try { await axios.delete(`${API_URL}/projects/${id}/members/${userId}`); toast.success('Miembro eliminado'); fetchProject(); }
    catch { toast.error('Error'); }
  };

  const handleToggleLeader = async (userId, currentRole) => {
    const newRole = currentRole === 'jefe_proyecto' ? 'miembro' : 'jefe_proyecto';
    try {
      await axios.patch(`${API_URL}/projects/${id}/members/${userId}/role?role=${newRole}`);
      toast.success(newRole === 'jefe_proyecto' ? 'Asignado como Jefe de Proyecto' : 'Rol cambiado a miembro');
      fetchProject();
    } catch { toast.error('Error al cambiar rol'); }
  };

  const memberIds = useMemo(() => project?.members?.map(m => m.user_id) || [], [project]);
  const availableUsers = useMemo(
    () => allUsers.filter(u => !memberIds.includes(u.id) && (u.role === 'solucionador' || u.role === 'jefatura')),
    [allUsers, memberIds]
  );
  const allFlatTasks = useMemo(() => project ? flattenTasks(project.tasks) : [], [project]);
  const criticalPath = useMemo(() => allFlatTasks.filter(t => t.is_critical).map(t => t.id), [allFlatTasks]);

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#7FD02E]" /></div>;
  if (!project) return null;

  const totalTasks = allFlatTasks.length;
  const completed = allFlatTasks.filter(t => t.status === 'completada').length;
  const progress = totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0;
  const daysCount = project.start_date && project.end_date
    ? Math.max(0, Math.ceil((new Date(project.end_date) - new Date(project.start_date)) / (1000 * 60 * 60 * 24)))
    : null;
  const leaders = project.members.filter(m => m.role_in_project === 'jefe_proyecto');
  const taskBudgetUsed = allFlatTasks.reduce((sum, t) => sum + (t.budget || 0), 0);
  const budgetFormatted = (val) => val ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val) : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-to-projects"><ArrowLeft className="w-5 h-5 text-gray-600" /></Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: project.color }} />
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-800">{project.name}</h1>
              {project.code && <Badge className="bg-gray-100 text-gray-600 border-gray-200 font-mono text-xs" data-testid="project-code-badge">{project.code}</Badge>}
            </div>
            <div className="flex items-center gap-4 mt-1 ml-7 flex-wrap">
              {project.description && <p className="text-gray-500 text-sm">{project.description}</p>}
              {leaders.length > 0 && (
                <span className="flex items-center gap-1 text-xs text-amber-600" data-testid="project-leaders">
                  <Crown className="w-3.5 h-3.5" />
                  {leaders.map(l => l.user?.first_name).filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMemberDialog(true)} className="border-gray-300 text-gray-600" data-testid="manage-members-btn">
            <Users className="w-4 h-4 mr-1" /> Equipo ({project.members.length})
          </Button>
          {isJefatura && (
            <Button variant="outline" size="sm" onClick={() => setBulkDialog(true)} className="border-gray-300 text-gray-600" data-testid="bulk-upload-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Cargar Excel
            </Button>
          )}
          <Button className="bg-[#7FD02E] hover:bg-[#6BC024] text-white" size="sm" onClick={() => openCreateTask()} data-testid="new-task-btn">
            <Plus className="w-4 h-4 mr-1" /> Nueva Tarea
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { val: totalTasks, label: 'Total Tareas', cls: 'text-gray-800' },
          { val: completed, label: 'Completadas', cls: 'text-emerald-600' },
          { val: allFlatTasks.filter(t => t.status === 'en_progreso').length, label: 'En Progreso', cls: 'text-amber-600' },
        ].map((s, i) => (
          <Card key={i} className="border-gray-200 bg-white shadow-sm"><CardContent className="p-4 text-center"><div className={`text-2xl font-bold ${s.cls}`}>{s.val}</div><div className="text-xs text-gray-500">{s.label}</div></CardContent></Card>
        ))}
        {daysCount !== null && (
          <Card className="border-gray-200 bg-white shadow-sm" data-testid="project-days-card">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{daysCount}</div>
              <div className="text-xs text-gray-500 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />Dias</div>
            </CardContent>
          </Card>
        )}
        <Card className="border-gray-200 bg-white shadow-sm" data-testid="budget-consumption-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 flex items-center gap-1"><DollarSign className="w-3 h-3" />Presupuesto</span>
              <span className="text-sm font-bold text-gray-800">{project.budget > 0 ? `${Math.round((taskBudgetUsed / project.budget) * 100)}%` : '0%'}</span>
            </div>
            <Progress value={project.budget > 0 ? Math.min(100, (taskBudgetUsed / project.budget) * 100) : 0} className="h-2 mb-1.5" />
            <div className="flex justify-between text-[10px] text-gray-400">
              <span>Usado: {budgetFormatted(taskBudgetUsed)}</span>
              <span>Total: {budgetFormatted(project.budget)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="flex">
        <Card className="border-gray-200 bg-white shadow-sm flex-1"><CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-500">Progreso</span><span className="text-sm font-bold text-gray-800">{progress}%</span></div><Progress value={progress} className="h-2" /></CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 border border-gray-200">
          <TabsTrigger value="gantt" className="data-[state=active]:bg-white data-[state=active]:text-[#7FD02E]" data-testid="tab-gantt"><BarChart3 className="w-4 h-4 mr-2" />Gantt</TabsTrigger>
          <TabsTrigger value="calendario" className="data-[state=active]:bg-white data-[state=active]:text-[#7FD02E]" data-testid="tab-calendario"><Calendar className="w-4 h-4 mr-2" />Calendario</TabsTrigger>
          <TabsTrigger value="lista" className="data-[state=active]:bg-white data-[state=active]:text-[#7FD02E]" data-testid="tab-lista">Lista</TabsTrigger>
        </TabsList>
        <TabsContent value="gantt" className="mt-4"><GanttView tasks={allFlatTasks} project={project} onEditTask={openEditTask} onUpdateTaskDates={handleUpdateTaskDates} onAddSubtask={openCreateTask} onDeleteTask={handleDeleteTask} isJefatura={isJefatura} dependencies={project.dependencies || []} criticalPath={criticalPath} /></TabsContent>
        <TabsContent value="calendario" className="mt-4"><CalendarView tasks={allFlatTasks} date={calendarDate} setDate={setCalendarDate} view={calendarView} setView={setCalendarView} onEditTask={openEditTask} /></TabsContent>
        <TabsContent value="lista" className="mt-4"><TaskListView tasks={project.tasks} onEditTask={openEditTask} onDeleteTask={handleDeleteTask} onAddSubtask={openCreateTask} isJefatura={isJefatura} /></TabsContent>
      </Tabs>

      {/* Task Dialog */}
      <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleTaskSubmit}>
            <DialogHeader>
              <DialogTitle className="text-gray-800">{editingTask ? 'Editar Tarea' : parentTaskId ? 'Nueva Subtarea' : 'Nueva Tarea'}</DialogTitle>
              <DialogDescription className="text-gray-500">{editingTask ? 'Modifica los datos' : parentTaskId ? 'Agrega una subtarea' : 'Agrega una tarea al proyecto'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label className="text-gray-700">Nombre *</Label>
                  <Input value={taskForm.name} onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })} placeholder="Nombre" required className="border-gray-300 focus:border-[#7FD02E]" data-testid="task-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Codigo</Label>
                  <Input value={taskForm.code} onChange={(e) => setTaskForm({ ...taskForm, code: e.target.value })} placeholder="T-001" className="border-gray-300 font-mono text-sm" data-testid="task-code-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Objetivo</Label>
                <Textarea value={taskForm.objective} onChange={(e) => setTaskForm({ ...taskForm, objective: e.target.value })} placeholder="Objetivo de la tarea..." rows={2} className="border-gray-300" data-testid="task-objective-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Descripcion</Label>
                <Textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} placeholder="Descripcion..." rows={2} className="border-gray-300" data-testid="task-description-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5" />URL</Label>
                <Input value={taskForm.url} onChange={(e) => setTaskForm({ ...taskForm, url: e.target.value })} placeholder="https://..." className="border-gray-300 text-sm" data-testid="task-url-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-gray-700">Inicio</Label><Input type="date" value={taskForm.start_date} onChange={(e) => setTaskForm({ ...taskForm, start_date: e.target.value })} className="border-gray-300" data-testid="task-start-date" /></div>
                <div className="space-y-2"><Label className="text-gray-700">Fin</Label><Input type="date" value={taskForm.end_date} onChange={(e) => setTaskForm({ ...taskForm, end_date: e.target.value })} className="border-gray-300" data-testid="task-end-date" /></div>
              </div>
              {taskForm.start_date && taskForm.end_date && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100" data-testid="task-days-count">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-blue-700 font-medium">
                    {Math.max(0, Math.ceil((new Date(taskForm.end_date) - new Date(taskForm.start_date)) / (86400000)))} dias
                  </span>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-gray-700">Asignar a (multiples)</Label>
                <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto p-2 space-y-1" data-testid="task-assignees-list">
                  {project.members.length === 0 ? <p className="text-xs text-gray-400 py-1">Sin miembros</p> : project.members.map(m => m.user && (
                    <label key={m.user_id} className={`flex items-center gap-3 p-1.5 rounded-lg cursor-pointer transition-colors ${taskForm.assignee_ids.includes(m.user_id) ? 'bg-[#7FD02E]/10' : 'hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={taskForm.assignee_ids.includes(m.user_id)} onChange={() => toggleAssignee(m.user_id)} className="rounded border-gray-300 text-[#7FD02E] focus:ring-[#7FD02E]" />
                      <span className="text-sm text-gray-700">{m.user.first_name} {m.user.last_name}</span>
                    </label>
                  ))}
                </div>
                {taskForm.assignee_ids.length > 0 && <p className="text-xs text-gray-400">{taskForm.assignee_ids.length} persona(s)</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Presupuesto (CLP)</Label>
                <Input type="number" min="0" value={taskForm.budget} onChange={(e) => setTaskForm({ ...taskForm, budget: e.target.value })} placeholder="0" className="border-gray-300 focus:border-[#7FD02E]" data-testid="task-budget-input" />
              </div>
              <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-red-50/50"
                style={{ borderColor: taskForm.is_critical ? '#dc2626' : '#e5e7eb', backgroundColor: taskForm.is_critical ? 'rgba(220,38,38,0.05)' : 'transparent' }}
                data-testid="task-is-critical-checkbox"
              >
                <input
                  type="checkbox"
                  checked={taskForm.is_critical}
                  onChange={(e) => setTaskForm({ ...taskForm, is_critical: e.target.checked })}
                  className="rounded border-gray-300 text-red-500 focus:ring-red-500 w-4 h-4"
                />
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-gray-700">Ruta Critica</span>
                </div>
              </label>
              <div className="space-y-2">
                <Label className="text-gray-700">Color</Label>
                <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(10, 1fr)' }} data-testid="color-palette">
                  {COLOR_PALETTE.flat().map((c) => (
                    <button key={c} type="button"
                      className={`w-full aspect-square rounded-sm border transition-all ${taskForm.color === c ? 'border-gray-800 ring-1 ring-gray-800 scale-110 z-10' : 'border-transparent hover:border-gray-300'}`}
                      style={{ backgroundColor: c }}
                      onClick={() => setTaskForm({ ...taskForm, color: c })}
                    />
                  ))}
                </div>
              </div>

              {/* Dependencies section - only for editing existing tasks */}
              {editingTask && isJefatura && (
                <div className="space-y-2" data-testid="task-dependencies-section">
                  <Label className="text-gray-700 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" />Dependencias (predecesoras)</Label>
                  {/* Current dependencies */}
                  {(editingTask.dependencies_in || []).length > 0 && (
                    <div className="space-y-1">
                      {editingTask.dependencies_in.map(dep => {
                        const fromTask = allFlatTasks.find(t => t.id === dep.from_task_id);
                        return (
                          <div key={dep.id} className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg border border-indigo-100" data-testid={`dep-item-${dep.id}`}>
                            <div className="flex items-center gap-2 text-sm">
                              <Link2 className="w-3.5 h-3.5 text-indigo-500" />
                              <span className="text-gray-700">{fromTask?.name || dep.from_task_id.slice(0,8)}</span>
                              <span className="text-xs text-indigo-400">Fin-Inicio</span>
                            </div>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-destructive"
                              onClick={() => handleRemoveDependency(editingTask.id, dep.id)} type="button"
                              data-testid={`remove-dep-${dep.id}`}
                            ><Unlink className="w-3.5 h-3.5" /></Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Add dependency dropdown */}
                  <Select onValueChange={(fromId) => handleAddDependency(editingTask.id, fromId)}>
                    <SelectTrigger className="border-gray-300 text-sm" data-testid="add-dependency-select">
                      <SelectValue placeholder="Agregar predecesora..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allFlatTasks
                        .filter(t => t.id !== editingTask.id && !(editingTask.dependencies_in || []).some(d => d.from_task_id === t.id))
                        .map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editingTask && (
                <>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Estado</Label>
                    <Select value={taskForm.status} onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}>
                      <SelectTrigger className="border-gray-300" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="en_progreso">En Progreso</SelectItem>
                        <SelectItem value="completada">Completada</SelectItem>
                        <SelectItem value="bloqueada">Bloqueada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Progreso: {taskForm.progress}%</Label>
                    <input type="range" min="0" max="100" step="5" value={taskForm.progress} onChange={(e) => setTaskForm({ ...taskForm, progress: parseInt(e.target.value) })} className="w-full accent-[#7FD02E]" data-testid="task-progress-slider" />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" className="text-gray-600" onClick={() => setTaskDialog(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving || !taskForm.name.trim()} className="bg-[#7FD02E] hover:bg-[#6BC024] text-white" data-testid="submit-task-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}{editingTask ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Member Dialog */}
      <Dialog open={memberDialog} onOpenChange={setMemberDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="text-gray-800">Equipo del Proyecto</DialogTitle><DialogDescription className="text-gray-500">Gestiona los miembros</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">Miembros actuales</Label>
              {project.members.length === 0 ? <p className="text-sm text-gray-400 py-2">Sin miembros</p> : (
                <div className="space-y-1">{project.members.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-2">
                      <UserAvatar user={m.user} size="sm" />
                      <span className="text-sm text-gray-700">{m.user?.first_name} {m.user?.last_name}</span>
                      {m.role_in_project === 'jefe_proyecto' && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs"><Crown className="w-3 h-3 mr-0.5" />Jefe</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {isJefatura && (
                        <Button
                          variant="ghost" size="sm"
                          className={`h-7 text-xs ${m.role_in_project === 'jefe_proyecto' ? 'text-amber-600' : 'text-gray-400 hover:text-amber-600'}`}
                          onClick={() => handleToggleLeader(m.user_id, m.role_in_project)}
                          data-testid={`toggle-leader-btn-${m.user_id}`}
                        >
                          <Crown className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {isJefatura && <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-destructive" onClick={() => handleRemoveMember(m.user_id)}><X className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </div>
                ))}</div>
              )}
            </div>
            {isJefatura && availableUsers.length > 0 && (
              <div className="space-y-2 border-t border-gray-100 pt-3">
                <Label className="text-gray-700 text-sm font-medium">Agregar miembros</Label>
                <div className="space-y-1 max-h-40 overflow-y-auto">{availableUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                    <span className="text-sm text-gray-600">{u.first_name} {u.last_name}</span>
                    <Button variant="ghost" size="sm" className="h-7 text-[#7FD02E] hover:bg-[#7FD02E]/10" onClick={() => handleAddMember(u.id)}><UserPlus className="w-3.5 h-3.5 mr-1" />Agregar</Button>
                  </div>
                ))}</div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <FileSpreadsheet className="w-5 h-5 text-[#7FD02E]" />
              Carga Masiva de Tareas
            </DialogTitle>
            <DialogDescription>Sube un archivo Excel (.xlsx) con tareas, subtareas y dependencias.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} className="w-full border-dashed border-[#7FD02E] text-[#7FD02E] hover:bg-[#7FD02E]/5" data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-2" /> Descargar Plantilla Excel
            </Button>
            <div className="space-y-2">
              <Label className="text-gray-700 text-sm">Archivo Excel</Label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${bulkFile ? 'border-[#7FD02E] bg-[#7FD02E]/5' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => document.getElementById('bulk-file-input').click()}
                data-testid="bulk-file-dropzone"
              >
                <input
                  id="bulk-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => setBulkFile(e.target.files[0] || null)}
                  data-testid="bulk-file-input"
                />
                {bulkFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-[#7FD02E]" />
                    <span className="text-sm font-medium text-gray-700">{bulkFile.name}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); setBulkFile(null); }}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Haz clic para seleccionar un archivo .xlsx</p>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-1">
              <p className="font-medium text-gray-600">Columnas del Excel:</p>
              <p><strong>nombre</strong> (obligatorio), fecha_inicio, fecha_fin, presupuesto, ruta_critica, depende_de, url</p>
              <p>Las tareas con <strong>depende_de</strong> se crean como subtareas con dependencia automatica.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkDialog(false); setBulkFile(null); }}>Cancelar</Button>
            <Button
              className="bg-[#7FD02E] hover:bg-[#6BC024] text-white"
              onClick={handleBulkUpload}
              disabled={!bulkFile || bulkUploading}
              data-testid="bulk-upload-submit-btn"
            >
              {bulkUploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Cargando...</> : <><Upload className="w-4 h-4 mr-1" />Cargar Tareas</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
